import { useCallback, useEffect, useState } from "react";
import { authFetchJson } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

type SignupBan = {
  email: string;
  attempts: string[];
  recent_attempts: number;
  banned_until: string | null;
  active: boolean;
  ban_count: number;
  last_unban_at: string | null;
  last_unban_by_email: string | null;
  updated_at: string | null;
};

export function AdminSignupBansTab() {
  const [items, setItems] = useState<SignupBan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyEmail, setBusyEmail] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetchJson<{ items: SignupBan[] }>("/api/admin/signup-bans");
      setItems(res.items ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Laden mislukt.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const unban = useCallback(async (email: string) => {
    if (!window.confirm(`Blokkade en pogingen wissen voor ${email}?`)) return;
    setBusyEmail(email);
    try {
      await authFetchJson(`/api/admin/signup-bans/${encodeURIComponent(email)}`, {
        method: "DELETE",
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Deblokkeren mislukt.");
    } finally {
      setBusyEmail(null);
    }
  }, [load]);

  return (
    <div className="space-y-6">
      <p className="text-sm text-[var(--cs-ink-muted)]">
        E-mailadressen met mislukte aanmeldpogingen (signup zonder passkey binnen
        1 uur). Drie pogingen binnen 24 uur blokkeren het adres 72 uur.
      </p>
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}
      {loading ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">Laden…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">Geen registraties.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[rgba(10,52,96,0.10)] bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[rgba(10,52,96,0.08)] text-left text-xs uppercase tracking-wide text-[var(--cs-ink-muted)]">
                <th className="px-4 py-3">E-mailadres</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Pogingen (24u)</th>
                <th className="px-4 py-3">Geblokkeerd tot</th>
                <th className="px-4 py-3">Totaal bans</th>
                <th className="px-4 py-3">Laatst gedeblokkeerd</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.email} className="border-b border-[rgba(10,52,96,0.05)] last:border-0">
                  <td className="px-4 py-3 font-medium text-[var(--cs-navy)]">{b.email}</td>
                  <td className="px-4 py-3">
                    {b.active ? (
                      <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">
                        Geblokkeerd
                      </span>
                    ) : (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                        Vrij
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">{b.recent_attempts}</td>
                  <td className="px-4 py-3">{b.banned_until ? formatDateTime(b.banned_until) : "—"}</td>
                  <td className="px-4 py-3">{b.ban_count}</td>
                  <td className="px-4 py-3">
                    {b.last_unban_at
                      ? `${formatDateTime(b.last_unban_at)}${b.last_unban_by_email ? ` (${b.last_unban_by_email})` : ""}`
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {b.active ? (
                      <button
                        type="button"
                        disabled={busyEmail === b.email}
                        onClick={() => void unban(b.email)}
                        className="rounded-lg border border-[rgba(10,52,96,0.10)] bg-white px-3 py-1.5 text-xs font-medium text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)] disabled:opacity-50"
                      >
                        {busyEmail === b.email ? "Bezig…" : "Deblokkeer"}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
